import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ReactComponent as EditIcon } from "../../../../assets/icons/edit.svg";
import { ReactComponent as TrashCanIcon } from "../../../../assets/icons/trash-can.svg";
import { StatusBadge } from "../../../../constants/statusConstants";
import { AuthContext } from "../../../../contexts/AuthContext";
import { serviceService } from "../../../../services/api/serviceService";
import { firebaseService } from "../../../../services/api/firebaseService";
import { formatCurrency } from "../../../../utils/CurrencyFormat";
import { formatDate } from "../../../../utils/DateFormat";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import Loading from "../../components/Loading";
import AddServicePopup from "./AddServicePopup";
import TabDetail from "./ViewDetailTabs/TabDetail";

const DetailService = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [data, setData] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const extractFirebaseImageUrls = (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string") return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");
    const firebaseUrls = [];
    images.forEach((img) => {
      const src = img.getAttribute("src");
      if (src && (src.startsWith("https://storage.googleapis.com/") || src.startsWith("https://firebasestorage.googleapis.com/"))) {
        firebaseUrls.push(src);
      }
    });
    return firebaseUrls;
  };

  const fetchServiceInfo = useCallback(
    async (serviceId) => {
      try {
        setLoading(true);
        const response = await serviceService.get_by_id(serviceId, token);
        setData(response || {});
      } catch (error) {
        toast.error("Không thể tải thông tin dịch vụ");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    if (id) fetchServiceInfo(id);
  }, [id, fetchServiceInfo]);

  const handleDelete = async () => {
    try {
      setLoading(true);

      const logoUrl = data.image?.url || data.image || "";
      const descriptionHtml = data.description || "";
      const notesHtml = data.notes || "";

      const contentImages = [
        ...extractFirebaseImageUrls(descriptionHtml),
        ...extractFirebaseImageUrls(notesHtml)
      ];

      const allImagesToDelete = [...contentImages];
      if (logoUrl && typeof logoUrl === "string" && logoUrl.startsWith("http")) {
        allImagesToDelete.push(logoUrl);
      }

      await serviceService.delete(id, token);

      for (const url of allImagesToDelete) {
        if (url.startsWith("https://storage.googleapis.com/") || url.startsWith("https://firebasestorage.googleapis.com/")) {
          try {
            await firebaseService.delete_file(token, url);
          } catch (err) {
            console.warn("Failed to delete image from Firebase:", url, err);
          }
        }
      }

      toast.success("Xóa dịch vụ thành công!");
      navigate("/quan-ly-dich-vu");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Không thể kết nối tới server",
      );
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6 h-full overflow-auto">
      <div className="flex justify-between items-center border-b pb-4">
        <div className="flex items-center space-x-4 w-1/3 max-w-[300px]">
          <img
            src={data?.image || data?.image?.url || "/images/logo.png"}
            alt="Service"
            className="w-[84px] h-[84px] rounded-lg object-contain"
          />
          <h3 className="text-xl font-semibold">{data.serviceName}</h3>
        </div>

        <div className="border-l h-[139px] mx-4" />

        <div className="w-1/3 text-sm flex flex-col gap-2">
          <p className="text-gray-500 uppercase font-medium">Thông tin chung</p>
          <div className="grid grid-cols-2 gap-2">
            <label>Loại dịch vụ</label>
            <span className="text-right">
              {data.category?.categoryName || "--"}
            </span>

            <label>Giá tiền</label>
            <span className="text-right">
              {data.price ? formatCurrency(data.price) : "--"}
            </span>

            <label>Trạng thái</label>
            <div className="flex justify-end">
              <StatusBadge status={data.status || "--"} />
            </div>
            <label>Loại form</label>
            <span className="text-right">{data.formName || "--"}</span>
            <label>Thứ tự ưu tiên</label>
            <span className="text-right">
              {data.priority === 0 ? "Không có" : data.priority || "Không có"}
            </span>
            <label>Thời gian nộp đơn</label>
            <span className="text-right">{data.submissionTime || "--"}</span>
            <label>Thời gian thẩm định</label>
            <span className="text-right">{data.reviewTime || "--"}</span>
          </div>
        </div>

        <div className="border-l h-[139px] mx-4" />

        <div className="w-1/3 text-sm flex flex-col gap-2">
          <p className="text-gray-500 uppercase font-medium">Dữ liệu</p>
          <div className="grid grid-cols-2 gap-2">
            <label>Tạo bởi</label>
            <span className="text-right">
              {data.createdBy?.fullName || "--"}
            </span>

            <label>Ngày tạo</label>
            <span className="text-right">
              {data.createdAt ? formatDate(data.createdAt) : "--"}
            </span>

            <label>Cập nhật bởi</label>
            <span className="text-right">
              {data.updatedBy?.fullName || "--"}
            </span>

            <label>Ngày cập nhật</label>
            <span className="text-right">
              {data.updatedAt ? formatDate(data.updatedAt) : "--"}
            </span>
          </div>
        </div>
      </div>

      <div className="border-b mt-4">
        <button
          className={`py-3 px-6 ${activeTab === "details"
            ? "border-b-2 border-blue-500 font-semibold text-blue-500"
            : "text-gray-500"
            }`}
          onClick={() => setActiveTab("details")}
        >
          Mô tả chi tiết
        </button>
      </div>

      {activeTab === "details" && (
        <TabDetail
          descripton={data.description || "Chưa có mô tả"}
          note={data.notes || "Chưa có ghi chú"}
        />
      )}

      <div className="mt-6 flex justify-end gap-4">
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="border border-red-500 text-red-500 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-red-500 hover:text-white"
        >
          <TrashCanIcon />
          Xóa dịch vụ
        </button>

        <button
          onClick={() => setIsPopupOpen(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600"
        >
          <EditIcon />
          Chỉnh sửa
        </button>
      </div>

      <AddServicePopup
        key={data?._id || "detail-service"}
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        onSubmit={() => fetchServiceInfo(id)}
        data={data}
      />

      <ConfirmDeleteModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        loading={loading}
        title="Xóa dịch vụ"
        message="Bạn có chắc chắn muốn xóa dịch vụ này không?"
      />
    </div>
  );
};

export default DetailService;
